/**
 * Canales de venta (listado paginado). Mismo envelope que el resto del API.
 *
 * @typedef {{ id: string, name: string, costCenter?: string, externalCode?: string, createdAt?: string, updatedAt?: string }} SaleChannelDto
 */
import axiosInstance from '../utils/axiosInstance';

/** Tamaño de página para traer todos los canales en pocas peticiones (`sale-channels?page=1&size=100`). */
export const SALE_CHANNELS_FETCH_PAGE_SIZE = 100;

/** Largo máximo BD/API para `costCenter` y `externalCode`. */
export const SALE_CHANNEL_EXTERNAL_CODE_MAX_LENGTH = 100;

/**
 * Body POST/PUT: siempre incluye name, costCenter y externalCode; opcionales vacíos → null.
 * @param {{ name?: string, costCenter?: string, externalCode?: string }} form
 * @returns {{ name: string, costCenter: string|null, externalCode: string|null }}
 */
export function buildSaleChannelWritePayload(form) {
  const name = String(form?.name ?? '').trim();
  const costCenterRaw = String(form?.costCenter ?? '').trim();
  const externalCodeRaw = String(form?.externalCode ?? '').trim();
  return {
    name,
    costCenter: costCenterRaw || null,
    externalCode: externalCodeRaw || null
  };
}

/**
 * @param {{ name?: string, costCenter?: string, externalCode?: string }} form
 * @param {(key: string, params?: object) => string} autoT
 * @returns {Record<string, string>}
 */
export function validateSaleChannelForm(form, autoT) {
  const errors = {};
  const name = String(form?.name ?? '').trim();
  if (!name) {
    errors.name = autoT('El nombre es obligatorio');
  }
  const ext = String(form?.externalCode ?? '').trim();
  if (ext.length > SALE_CHANNEL_EXTERNAL_CODE_MAX_LENGTH) {
    errors.externalCode = autoT('Máximo {{max}} caracteres', { max: SALE_CHANNEL_EXTERNAL_CODE_MAX_LENGTH });
  }
  const cc = String(form?.costCenter ?? '').trim();
  if (cc.length > SALE_CHANNEL_EXTERNAL_CODE_MAX_LENGTH) {
    errors.costCenter = autoT('Máximo {{max}} caracteres', { max: SALE_CHANNEL_EXTERNAL_CODE_MAX_LENGTH });
  }
  return errors;
}

/**
 * Obtiene todos los canales paginando hasta cubrir `totalCount` (o hasta una página parcial).
 *
 * @param {{ signal?: AbortSignal, pageSize?: number }} [options]
 * @returns {Promise<SaleChannelDto[]>}
 */
export async function fetchAllSaleChannels(options = {}) {
  const { signal, pageSize = SALE_CHANNELS_FETCH_PAGE_SIZE } = options;
  /** @type {SaleChannelDto[]} */
  const all = [];
  let page = 1;

  for (;;) {
    const { data } = await axiosInstance.get(`sale-channels?page=${page}&size=${pageSize}`, {
      signal
    });

    const records = data?.records;
    if (!records || records.code !== 'SUCCESS') {
      const msg = records?.message;
      throw new Error(typeof msg === 'string' && msg.trim() ? msg : 'Error al obtener canales de venta');
    }

    const batch = Array.isArray(records.data) ? records.data : [];
    all.push(...batch);

    const total =
      typeof data?.totalCount === 'number' && Number.isFinite(data.totalCount) && data.totalCount >= 0
        ? data.totalCount
        : null;

    if (total != null && all.length >= total) break;
    if (batch.length === 0) break;
    if (batch.length < pageSize) break;

    page += 1;
    if (page > 500) break;
  }

  all.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }));
  return all;
}
